import React, { forwardRef, PropsWithChildren } from "react";
import { IconCalendar } from "../../icons";
import styles from "./DatePickerInput.module.scss";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Input } from "../../input";
import { useControllableState } from "../../../helpers/state/state-hooks";
import { DEFAULT_DATE_FORMAT } from "../date-picker.util";
import classNames from "classnames";

const DatePickerInputTrigger = PopoverPrimitive.Trigger;

export interface DatePickerInputProps
  extends PopoverPrimitive.PopoverContentProps {
  includePortal?: boolean;
  loop?: boolean;
  date?: string;
}

export const DatePickerInput = ({
  children,
  ...rest
}: PropsWithChildren<PopoverPrimitive.PopoverProps>) => {
  return (
    <PopoverPrimitive.Root  {...rest} >
      {children}
    </PopoverPrimitive.Root>
  );
};
export const DatePickerInputContent = forwardRef<
  React.ElementRef<typeof PopoverPrimitive.PopoverContent>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.PopoverContent>
>((componentProps: PropsWithChildren<DatePickerInputProps>, ref) => {
  const {
    children,
    className = undefined,
    includePortal = true,
    loop = true,
    ...rest
  } = componentProps;

  const classes = classNames(styles.content, className);

  const popoverContent = (
    <PopoverPrimitive.PopoverContent className={classes} align="start" {...rest} ref={ref}>
      {children}
    </PopoverPrimitive.PopoverContent>
  );

  return (
    <>
      {includePortal ? (
        <PopoverPrimitive.Portal forceMount={rest.forceMount}
        {...rest}
        data-testid={"datepickerContent"}
        >
          {popoverContent}
        </PopoverPrimitive.Portal>
      ) : (
        <>{popoverContent}</>
      )}
    </>
  );
});

DatePickerInputContent.displayName = "DatePickerInputContent"

interface DatePickerInputAreaProps {
  placeholder?: string;
  date?: string;
  handleDateChange?: (newDate: string) => void;
  value?: string | Date;
}
export const DatePickerInputArea = ({ placeholder, date, handleDateChange }: DatePickerInputAreaProps) => {
  const [dateInput, setDateInput] = useControllableState({
    prop: date,
    defaultProp: "",
    onChange: handleDateChange,
  });

  return (
    <DatePickerInputTrigger className={styles.datepickerInput} asChild>
      <div className={styles.datepickerInput}>
        <Input
          placeholder={placeholder ?? DEFAULT_DATE_FORMAT}
          Icon={IconCalendar}
          value={dateInput ?? ''}
          onChange={(e) => {setDateInput(e.target.value); handleDateChange && handleDateChange(e.target.value)}}
        />
      </div>
    </DatePickerInputTrigger>
  )
}